import axios, { AxiosError } from "axios"
import { v4 as uuidv4 } from "uuid"
import {
  AttachmentControllerRoute,
  AttachmentControllerRouteToMetadataTypesMap,
} from ".."
import { useQueryClient } from "@tanstack/react-query"
import { DefaultError } from "../../-components/error-context"
import {
  AttachmentUploadContext,
  IAttachmentUploadQueueItem,
  TAttachmentUploadQueueItemStatus,
} from "./attachment-upload-context"
import { ProblemDetails } from "@/@client/Microsoft_AspNetCore_Mvc"

const MAX_CHUNK_SIZE = 200 * 1024 * 1024 // 200 MB

interface Props extends React.PropsWithChildren {}

export function AttachmentUploadContextProvider({ children }: Props) {
  const queryClient = useQueryClient()

  const [uploadHistory, setUploadHistory] = React.useState<
    IAttachmentUploadQueueItem<AttachmentControllerRoute>[]
  >([])

  const [uploadQueue, setUploadQueue] = React.useState<
    IAttachmentUploadQueueItem<AttachmentControllerRoute>[]
  >([])

  const [abortController, setAbortController] = React.useState<
    AbortController | undefined
  >()

  const [currentUpload, setCurrentUpload] = React.useState<
    IAttachmentUploadQueueItem<AttachmentControllerRoute> | undefined
  >(undefined)

  function addToUploadQueue(
    files: File[],
    attachmentControllerRoute: AttachmentControllerRoute,
    containerId: number | undefined,
    metadata?: (typeof AttachmentControllerRouteToMetadataTypesMap)[AttachmentControllerRoute]["partialMetadata"],
    handlers?: {
      onSuccess?: (
        metadata: (typeof AttachmentControllerRouteToMetadataTypesMap)[AttachmentControllerRoute]["metadata"]
      ) => void
      onComplete?: () => void
      onError?: (error: DefaultError) => void
    }
  ) {
    setUploadQueue((prevQueue) => [
      ...prevQueue,
      ...files.map((file) => ({
        guid: uuidv4(),
        file,
        containerId,
        progress: 0,
        status: "pending" as TAttachmentUploadQueueItemStatus,
        attachmentControllerRoute,
        metadata,
        handlers,
      })),
    ])
  }

  function cancelUpload(guid: string) {
    if (currentUpload?.guid === guid) {
      if (abortController) {
        abortController.abort()

        setCurrentUpload((prev) =>
          prev ? { ...prev, status: "canceled" } : undefined
        )
      }
    } else {
      setUploadQueue((prev) => prev.filter((item) => item.guid !== guid))
    }
  }

  async function processFileUpload(
    attachmentuploadQueueItem: IAttachmentUploadQueueItem<AttachmentControllerRoute>
  ) {
    const file = attachmentuploadQueueItem.file
    const totalChunks = Math.ceil(file.size / MAX_CHUNK_SIZE)

    const abortController = new AbortController()
    setAbortController(abortController)

    let result:
      | (typeof AttachmentControllerRouteToMetadataTypesMap)[AttachmentControllerRoute]["metadata"]
      | undefined = undefined

    for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
      const start = chunkIndex * MAX_CHUNK_SIZE
      const end = Math.min(file.size, start + MAX_CHUNK_SIZE)
      const chunk = file.slice(start, end)

      const formData = new FormData()
      formData.append("file", chunk)
      formData.append("fileName", file.name)
      formData.append("chunkIndex", chunkIndex.toString())
      formData.append("totalChunks", totalChunks.toString())
      formData.append("fileSize", file.size.toString())
      formData.append("guid", attachmentuploadQueueItem.guid)

      attachmentuploadQueueItem.metadata &&
        formData.append(
          "metadata",
          JSON.stringify(attachmentuploadQueueItem.metadata)
        )

      try {
        const axiosResponse = await axios.post<
          (typeof AttachmentControllerRouteToMetadataTypesMap)[AttachmentControllerRoute]["metadata"]
        >(
          `/api/${attachmentuploadQueueItem.attachmentControllerRoute}/attachments/add-attachment-file-and-metadata`,
          formData,
          {
            signal: abortController.signal,
          }
        )
        result = axiosResponse.data
      } catch (error: unknown) {
        cancelUpload(attachmentuploadQueueItem.guid)

        if (axios.isCancel(error)) {
          axios.post(
            `/api/${attachmentuploadQueueItem.attachmentControllerRoute}/attachments/cancel`,
            {
              params: {
                guid: attachmentuploadQueueItem.guid,
              },
            }
          )
        } else {
          abortController.abort()
          attachmentuploadQueueItem.handlers?.onError &&
            attachmentuploadQueueItem.handlers?.onError(
              new DefaultError(
                (error as AxiosError).response?.data as ProblemDetails
              )
            )
          attachmentuploadQueueItem.handlers?.onComplete &&
            attachmentuploadQueueItem.handlers?.onComplete()

          throw error
        }
      }

      setCurrentUpload((prev) =>
        prev
          ? {
              ...prev,
              progress: Math.round(((chunkIndex + 1) / totalChunks) * 100),
              status:
                chunkIndex + 1 === totalChunks ? "completed" : "uploading",
            }
          : undefined
      )
    }

    queryClient.invalidateQueries({
      queryKey: ["attachments", attachmentuploadQueueItem.containerId],
    })

    result &&
      attachmentuploadQueueItem.handlers?.onSuccess &&
      attachmentuploadQueueItem.handlers?.onSuccess(result)

    attachmentuploadQueueItem.handlers?.onComplete &&
      attachmentuploadQueueItem.handlers?.onComplete()
  }

  React.useEffect(() => {
    if (!currentUpload && uploadQueue.length) {
      setCurrentUpload(uploadQueue[0])
      setUploadQueue((prev) => prev.slice(1))
    } else if (currentUpload && currentUpload.status !== "uploading") {
      if (currentUpload.status === "pending") {
        processFileUpload(currentUpload)
      }
      if (
        currentUpload.status === "completed" ||
        currentUpload.status === "canceled"
      ) {
        setUploadHistory((prev) => [...prev, currentUpload])
        setCurrentUpload(undefined)
        setAbortController(undefined)
      }
    }
  }, [uploadQueue, currentUpload])

  return (
    <AttachmentUploadContext.Provider
      value={{
        addToUploadQueue,
        cancelUpload,
        uploadQueue,
        currentUpload,
        uploadHistory,
      }}
    >
      {children}
    </AttachmentUploadContext.Provider>
  )
}
